import { type ReactNode, useMemo } from 'react';
import * as THREE from 'three';
import type { Anim } from './anim';
import { Animated } from './animated';
import { CLASSICAL, INDUSTRIAL } from './materials';

/** Props every unit and building model takes. */
export type ModelProps = {
  /** Flat player-coloured material. */
  accent: THREE.Material;
  /** Emissive player-coloured material, for lights. */
  glow: THREE.Material;
  /** Building level (1 for units). */
  level: number;
};

/** A look table's entry for `level`: entry i is level i + 1, levels past the end use the last. */
export function levelEntry<T>(table: readonly T[], level: number): T {
  return table[Math.min(Math.max(level, 1), table.length) - 1];
}

// Pyramid roof: a 4-sided cone turned so its edges line up with a box.
export function pyramid(radius: number, height: number): THREE.BufferGeometry {
  const g = new THREE.ConeGeometry(radius, height, 4);
  g.rotateY(Math.PI / 4);
  return g;
}

/**
 * A hip roof: `w` × `d` at the eaves (base at y 0), rising `h` to a ridge `r`
 * long along x.
 */
export function hipRoof(w: number, d: number, h: number, r: number): THREE.BufferGeometry {
  const [x, z, rx] = [w / 2, d / 2, r / 2];
  const A = [-x, 0, z];
  const B = [x, 0, z];
  const C = [x, 0, -z];
  const D = [-x, 0, -z];
  const E = [-rx, h, 0];
  const F = [rx, h, 0];
  // Front, back, right and left faces, wound to face outwards.
  const tris = [A, B, F, A, F, E, C, D, E, C, E, F, B, C, F, D, A, E];
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(tris.flat(), 3));
  g.computeVertexNormals();
  return g;
}

/** A gable roof along x: a triangular prism `length` long, apex up, eaves `radius` out. */
export function gableRoof(radius: number, length: number): THREE.BufferGeometry {
  return new THREE.CylinderGeometry(radius, radius, length, 3)
    .rotateZ(Math.PI / 2)
    .rotateX(-Math.PI / 2);
}

const G = {
  body: new THREE.CylinderGeometry(0.085, 0.12, 0.3, 8),
  head: new THREE.SphereGeometry(0.075, 10, 8),
  shaft: new THREE.CylinderGeometry(0.012, 0.012, 1, 5),
  dummyHead: new THREE.SphereGeometry(0.045, 8, 6),
  hexPad: new THREE.CylinderGeometry(0.72, 0.72, 0.03, 6),
  puff: new THREE.IcosahedronGeometry(0.045, 0),
  beam: new THREE.BoxGeometry(1, 1, 1),
};

const UP = new THREE.Vector3(0, 1, 0);

type Point = readonly [number, number, number];

/** A straight square beam `thickness` thick from `from` to `to` (stays, cables, conveyors). */
export function Beam({
  from,
  to,
  material,
  thickness = 0.02,
}: {
  from: Point;
  to: Point;
  material: THREE.Material;
  thickness?: number;
}) {
  const a = new THREE.Vector3(...from);
  const dir = new THREE.Vector3(...to).sub(a);
  const length = dir.length();
  const quaternion = new THREE.Quaternion().setFromUnitVectors(UP, dir.normalize());
  const mid = a.addScaledVector(dir, length / 2);
  return (
    <mesh
      geometry={G.beam}
      material={material}
      position={mid}
      quaternion={quaternion}
      scale={[thickness, length, thickness]}
    />
  );
}

/** A thin hexagonal pad covering a whole tile (a grown building's extra tiles). */
export function HexPad({ material }: { material: THREE.Material }) {
  return <mesh geometry={G.hexPad} material={material} position={[0, 0.015, 0]} />;
}

export function Figure({ accent }: { accent: THREE.Material }) {
  return (
    <>
      <mesh geometry={G.body} material={accent} position={[0, 0.15, 0]} />
      <mesh geometry={G.head} material={CLASSICAL.SKIN} position={[0, 0.37, 0]} />
    </>
  );
}

/** A thin pole `length` long, centred on `position`, tilted by `tilt` about z. */
export function Pole({
  length,
  position,
  tilt = 0,
  material = CLASSICAL.WOOD,
}: {
  length: number;
  position: [number, number, number];
  tilt?: number;
  material?: THREE.Material;
}) {
  return (
    <mesh
      geometry={G.shaft}
      material={material}
      position={position}
      rotation={[0, 0, tilt]}
      scale={[1, length, 1]}
    />
  );
}

/** A straw training dummy on a post (barracks yard). */
export function TrainingDummy({ position }: { position: [number, number, number] }) {
  return (
    <group position={position}>
      <Pole length={0.2} position={[0, 0.1, 0]} />
      <Pole length={0.14} position={[0, 0.14, 0]} tilt={Math.PI / 2} />
      <mesh geometry={G.dummyHead} material={CLASSICAL.STRAW} position={[0, 0.23, 0]} />
    </group>
  );
}

/** Slot offsets for `count` squad members: one at the front (+z), the rest in a ring. */
function squadSlots(count: number, spread: number): [number, number, number][] {
  if (count <= 1) return [[0, 0, 0]];
  return Array.from({ length: count }, (_, i) => {
    const a = (i * Math.PI * 2) / count;
    return [spread * Math.sin(a), 0, spread * Math.cos(a)];
  });
}

/**
 * `count` scaled-down figures in formation, for squad units. `children` (gear:
 * helmets, rifles, …) is drawn on every member, in the member's own frame.
 */
export function Squad({
  accent,
  count = 3,
  scale = 0.7,
  spread = 0.16,
  children,
}: {
  accent: THREE.Material;
  count?: number;
  scale?: number;
  spread?: number;
  children?: ReactNode;
}) {
  return (
    <>
      {squadSlots(count, spread).map((pos) => (
        <group key={`${pos[0]}:${pos[2]}`} position={pos} scale={scale}>
          <Figure accent={accent} />
          {children}
        </group>
      ))}
    </>
  );
}

// One id per puff of a stack (their count is the puffs per stack).
const PUFF_IDS = ['low', 'mid', 'high'];
const PUFFS_PER_STACK = PUFF_IDS.length;
const PUFF_RISE = 0.28;
const PUFF_DRIFT = 0.05;
const PUFF_SPEED = 0.35; // cycles per second

/**
 * Smoke puffs rising from the tops of chimneys at `stacks`, swelling as they
 * climb and shrinking away at the top.
 */
export function Smoke({ stacks }: { stacks: readonly [number, number, number][] }) {
  const puffs = useMemo(
    () =>
      stacks.flatMap((stack, s) =>
        PUFF_IDS.map((id, p) => {
          const i = s * PUFFS_PER_STACK + p;
          // Stagger stacks too, so neighbouring chimneys don't puff in step.
          const offset = p / PUFFS_PER_STACK + i * 0.13;
          const anim: Anim = {
            smoke: { speed: PUFF_SPEED, offset, rise: PUFF_RISE, drift: PUFF_DRIFT },
          };
          return { key: `${stack[0]}:${stack[2]}:${id}`, stack, anim };
        })
      ),
    [stacks]
  );
  return (
    <>
      {puffs.map(({ key, stack, anim }) => (
        <Animated key={key} anim={anim} position={stack}>
          <mesh geometry={G.puff} material={INDUSTRIAL.SMOKE} />
        </Animated>
      ))}
    </>
  );
}
